import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { bus } from '../../App';

const STREAMING_TEXT_EVENTS = new Set([
  'llm/delta/text',
  'llm/delta/json',
]);

const STORAGE_KEY = 'live-panel-events';

interface ContentPart {
  type: 'event' | 'text';
  content: string;
  payload?: any;
  id?: string;
}

interface StoredEvent {
  event: string;
  payload?: any;
  timestamp: number;
}

function EventBadge({ eventName, payload }: {
  eventName: string;
  payload?: any;
}) {
  const [expanded, setExpanded] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  let prettyContent = '';
  try {
    prettyContent = JSON.stringify(payload, null, 2);
  } catch {
    prettyContent = String(payload ?? '');
  }

  // Calculate position based on button
  useEffect(() => {
    if (!expanded || !buttonRef.current || !detailsRef.current) return;

    const updatePosition = () => {
      const buttonRect = buttonRef.current!.getBoundingClientRect();
      const detailsRect = detailsRef.current!.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Calculate available space in all directions
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const spaceRight = viewportWidth - buttonRect.left;
      const spaceLeft = buttonRect.right;

      const margin = 8;

      // Vertical positioning: choose the side with more space
      let top: number;
      if (spaceBelow >= detailsRect.height + margin) {
        // Position below if there's enough space
        top = buttonRect.bottom + 4;
      } else if (spaceAbove >= detailsRect.height + margin) {
        // Position above if there's enough space
        top = buttonRect.top - detailsRect.height - 4;
      } else if (spaceBelow > spaceAbove) {
        // Not enough space either way, position below and constrain
        top = Math.max(margin, viewportHeight - detailsRect.height - margin);
      } else {
        // Position above and constrain
        top = margin;
      }

      // Horizontal positioning: choose the side with more space
      let left: number;
      if (spaceRight >= detailsRect.width) {
        // Position from left edge if there's enough space
        left = buttonRect.left;
      } else if (spaceLeft >= detailsRect.width) {
        // Position from right edge (right-align with button)
        left = buttonRect.right - detailsRect.width;
      } else {
        // Not enough space, center or constrain
        left = Math.max(margin, Math.min(buttonRect.left, viewportWidth - detailsRect.width - margin));
      }

      setPosition({ top, left });
    };

    // Need to update position after the portal renders to get accurate dimensions
    requestAnimationFrame(() => {
      updatePosition();
    });

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [expanded]);

  // Close on outside click
  useEffect(() => {
    if (!expanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        detailsRef.current &&
        !detailsRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expanded]);

  // Close on Escape key
  useEffect(() => {
    if (!expanded) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpanded(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [expanded]);

  const portalContent = expanded && createPortal(
    <div
      ref={detailsRef}
      className="tool-call-badge-details"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <pre className="tool-call-code">
        <code>{prettyContent}</code>
      </pre>
    </div>,
    document.body
  );

  return (
    <span className="tool-call-badge">
      <button
        ref={buttonRef}
        className="tool-call-badge-button"
        onClick={() => setExpanded(!expanded)}
        type="button"
        title={expanded ? 'Hide details' : 'Show details'}
      >
        {eventName}
      </button>
      {portalContent}
    </span>
  );
}

function StreamingText({ content, id }: { content: string; id: string }) {
  const [collapsed, setCollapsed] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!collapsed) return;
    const element = containerRef.current;
    if (!element) return;
    element.scrollTop = element.scrollHeight;
  }, [content, collapsed]);

  return (
    <div
      ref={containerRef}
      className={`live-panel-streaming-text ${collapsed ? 'live-panel-streaming-text--collapsed' : ''}`}
      onClick={() => setCollapsed(!collapsed)}
      title={collapsed ? 'Click to expand' : 'Click to collapse'}
    >
      {content}
    </div>
  );
}

export function LivePanel() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const [contentParts, setContentParts] = useState<ContentPart[]>([]);
  const [eventCount, setEventCount] = useState(0);
  const eventsArrayRef = useRef<StoredEvent[]>([]);
  const textIdCounterRef = useRef(0);

  // Load events from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const events: StoredEvent[] = JSON.parse(stored);
        eventsArrayRef.current = events;

        // Reconstruct content parts from stored events
        const parts: ContentPart[] = [];
        for (const evt of events) {
          const isStreamingText = STREAMING_TEXT_EVENTS.has(evt.event);
          if (isStreamingText) {
            const textChunk = typeof evt.payload === 'string' ? evt.payload : String(evt.payload ?? '');
            if (parts.length > 0 && parts[parts.length - 1].type === 'text') {
              parts[parts.length - 1].content += textChunk;
            } else {
              parts.push({ type: 'text', content: textChunk, id: `text-${textIdCounterRef.current++}` });
            }
          } else {
            parts.push({ type: 'event', content: evt.event, payload: evt.payload });
          }
        }
        setContentParts(parts);
        setEventCount(events.length);
      }
    } catch (error) {
      console.error('Failed to load events from localStorage:', error);
    }
  }, []);

  // Subscribe to all bus events
  useEffect(() => {
    const unsubscribe = bus.monitor((event: string, payload?: any) => {
      if (paused) return;

      setEventCount((prev) => prev + 1);

      // Add to events array
      eventsArrayRef.current.push({
        event,
        payload,
        timestamp: Date.now(),
      });

      // Save to localStorage on llm/end
      if (event === 'llm/end' || event === 'llm/error') {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(eventsArrayRef.current));
        } catch (error) {
          console.error('Failed to save events to localStorage:', error);
        }
      }

      const isStreamingText = STREAMING_TEXT_EVENTS.has(event);

      if (isStreamingText) {
        // Append payload directly without newline for streaming text
        const textChunk = typeof payload === 'string' ? payload : String(payload ?? '');
        setContentParts((prev) => {
          if (prev.length > 0 && prev[prev.length - 1].type === 'text') {
            // Append to existing text part
            const newParts = [...prev];
            newParts[newParts.length - 1] = {
              ...newParts[newParts.length - 1],
              content: newParts[newParts.length - 1].content + textChunk,
            };
            return newParts;
          }
          return [...prev, { type: 'text', content: textChunk, id: `text-${textIdCounterRef.current++}` }];
        });
      } else {
        // Add event name for non-streaming events
        setContentParts((prev) => [
          ...prev,
          { type: 'event', content: event, payload },
        ]);
      }
    });

    return unsubscribe;
  }, [paused]);

  // Auto-scroll to bottom when content changes (unless paused)
  useEffect(() => {
    if (paused) return;

    const container = containerRef.current;
    if (!container) return;

    container.scrollTop = container.scrollHeight;
  }, [contentParts, paused]);

  const handleTogglePause = () => {
    setPaused(!paused);
  };

  const handleClear = () => {
    setContentParts([]);
    setEventCount(0);
    eventsArrayRef.current = [];
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  };

  return (
    <div className="panel-shell">
      <div className="panel-controls">
        <button
          className="btn"
          onClick={handleTogglePause}
          title={paused ? 'Resume live view' : 'Pause live view'}
        >
          {paused ? 'Resume' : 'Pause'}
        </button>
        <button
          className="btn"
          onClick={handleClear}
          title="Clear event buffer"
        >
          Clear ({eventCount})
        </button>
      </div>
      <div ref={containerRef} className="live-panel-content">
        {contentParts.length === 0 ? (
          <div className="live-panel-empty">Waiting for events...</div>
        ) : (
          <pre className="live-panel-text">
            {contentParts.map((part, index) =>
              part.type === 'event' ? (
                <div key={index} className="live-panel-event-badges">
                  <EventBadge
                    eventName={part.content}
                    payload={part.payload}
                  />
                </div>
              ) : (
                <StreamingText
                  key={part.id || index}
                  content={part.content}
                  id={part.id || `text-${index}`}
                />
              )
            )}
          </pre>
        )}
      </div>
    </div>
  );
}
